import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Camera, FileText, Lock, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ALLOWED, MAX_SIZE, clearPending, getPending, savePending } from "@/lib/pendingDoc";
import { track } from "@/lib/api";
import { toast } from "sonner";

const Ready = ({ file, onReset }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!file.type.startsWith("image/")) return undefined;
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return (
    <div data-testid="guest-upload-ready">
      <div className="flex items-center gap-3">
        {url ? <img src={url} alt="La tua lettera" className="h-16 w-14 rounded-lg object-cover border" /> : <span className="h-16 w-14 rounded-lg border bg-slate-50 grid place-items-center"><FileText className="h-6 w-6 text-[#1E3A8A]" /></span>}
        <div className="min-w-0 flex-1">
          <p className="font-medium truncate" data-testid="guest-upload-filename">{file.name}</p>
          <p className="text-sm text-emerald-700">Lettera ricevuta. La tua analisi è pronta da avviare.</p>
        </div>
        <button onClick={onReset} className="text-slate-400 hover:text-slate-700" aria-label="Cambia file" data-testid="guest-upload-reset"><X className="h-5 w-5" /></button>
      </div>
      <Button asChild className="mt-4 w-full h-auto min-h-12 py-3 whitespace-normal rounded-full bg-[#E07A5F] hover:bg-[#d06a50] text-base" data-testid="guest-upload-continue">
        <Link to="/accedi?mode=register" onClick={() => track("guest_continue")}>Crea account gratis e vedi cosa fare <ArrowRight className="ml-2 h-4 w-4 shrink-0" /></Link>
      </Button>
      <p className="mt-2 text-xs text-slate-500 text-center">30 secondi con email o Google. L'analisi parte da sola subito dopo.</p>
    </div>
  );
};

export const GuestUpload = () => {
  const [file, setFile] = useState(null);
  const [drag, setDrag] = useState(false);
  const camRef = useRef();
  const fileRef = useRef();
  useEffect(() => {
    getPending().then((f) => f && setFile(f));
  }, []);
  const pick = async (f) => {
    if (!f) return;
    if (!ALLOWED.includes(f.type)) return toast.error("Formato non supportato. Usa una foto (JPG, PNG) o un PDF.");
    if (f.size > MAX_SIZE) return toast.error("File troppo grande (massimo 10 MB)");
    await savePending(f).catch(() => {});
    track("guest_upload");
    setFile(f);
  };
  const reset = () => { clearPending(); setFile(null); };

  return (
    <div className={`mt-10 w-full max-w-xl min-w-0 rounded-3xl border-2 border-dashed bg-white/80 backdrop-blur p-5 md:p-6 transition-colors duration-200 ${drag ? "border-[#1E3A8A] bg-[#1E3A8A]/5" : "border-slate-300"}`}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files[0]); }} data-testid="guest-upload">
      {file ? <Ready file={file} onReset={reset} /> : (
        <>
          <p className="font-medium">Hai una lettera da capire? Provala ora</p>
          <p className="text-sm text-slate-500 mt-1">Multa, cartella, bolletta, avviso INPS… anche in foto.</p>
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <Button onClick={() => camRef.current.click()} className="h-12 rounded-full px-6 bg-[#1E3A8A] hover:bg-[#172f70] text-base" data-testid="guest-upload-camera-btn"><Camera className="h-4 w-4 mr-2" /> Scatta una foto</Button>
            <Button variant="outline" onClick={() => fileRef.current.click()} className="h-12 rounded-full px-6 text-base" data-testid="guest-upload-file-btn"><Upload className="h-4 w-4 mr-2" /> Carica file</Button>
          </div>
          <p className="mt-3 text-xs text-slate-500 flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Il file resta sul tuo dispositivo finché non crei l'account.</p>
          <input ref={camRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => pick(e.target.files[0])} data-testid="guest-upload-camera-input" />
          <input ref={fileRef} type="file" accept={ALLOWED.join(",")} hidden onChange={(e) => pick(e.target.files[0])} data-testid="guest-upload-file-input" />
        </>
      )}
    </div>
  );
};
